import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ToggleRight, ToggleLeft } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../../context/AuthContext";
import ClientSelector from "./ClientSelector";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ServicesSettings() {
  const [clientId, setClientId] = useState("");
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = clientId ? `?client_id=${encodeURIComponent(clientId)}` : "";
    axios.get(`${API}/admin/services${params}`, { withCredentials: true })
      .then((r) => setServices(r.data))
      .catch((e) => toast.error("Load failed: " + formatApiError(e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (svc) => {
    const next = !svc.enabled;
    setServices((arr) => arr.map((s) => s.id === svc.id ? { ...s, enabled: next } : s));
    setSaving(svc.id);
    try {
      await axios.put(`${API}/admin/services/${svc.id}`,
        { enabled: next, client_id: clientId || null }, { withCredentials: true });
      toast.success(`${svc.label} → ${next ? "ATTIVO" : "DISATTIVATO"}`);
    } catch (e) {
      toast.error("Save failed: " + formatApiError(e?.response?.data?.detail || e.message));
      setServices((arr) => arr.map((s) => s.id === svc.id ? { ...s, enabled: !next } : s));
    } finally {
      setSaving(null);
    }
  };

  const enabledCount = services.filter((s) => s.enabled).length;

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 sm:py-8 lg:py-10 max-w-4xl">
      <header className="mb-8">
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#00E5FF] mb-2">
          ADMIN · SERVICE VISIBILITY
        </div>
        <h1 className="text-4xl font-black tracking-tight">Gestione Servizi</h1>
        <p className="text-white/50 text-sm mt-2 max-w-2xl">
          Attiva o disattiva la visibilità di ogni servizio per il cliente.
          Le sezioni disattivate saranno completamente nascoste dalla sidebar
          e le relative API restituiranno 403.
        </p>
        <div className="mt-4 text-xs text-white/40">
          <span className="text-[#00E5FF] font-bold">{enabledCount}</span>
          <span> / {services.length} servizi attivi</span>
        </div>
      </header>

      <ClientSelector value={clientId} onChange={setClientId} />

      {loading && <div className="text-white/40 text-sm">Loading…</div>}

      <div className="bg-[#0A0E17] border border-white/10 rounded overflow-hidden" data-testid="services-table">
        {services.map((s) => (
          <div
            key={s.id}
            data-testid={`service-row-${s.id}`}
            className="flex items-center justify-between px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/[0.02]"
          >
            <div>
              <div className="text-white/90 font-bold text-sm">{s.label}</div>
              <div className="text-[10px] text-white/30 uppercase tracking-widest mt-0.5">
                {s.id}
              </div>
            </div>
            <button
              onClick={() => toggle(s)}
              disabled={saving === s.id}
              data-testid={`toggle-${s.id}`}
              className={`flex items-center gap-2 px-4 py-2 rounded font-bold text-xs transition ${
                s.enabled
                  ? "bg-[#00E5FF]/10 text-[#00E5FF] border border-[#00E5FF]/40 hover:bg-[#00E5FF]/20"
                  : "bg-white/[0.03] text-white/40 border border-white/10 hover:bg-white/5"
              } disabled:opacity-40`}
            >
              {s.enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
              {s.enabled ? "ATTIVO" : "DISATTIVATO"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
